import { setContext } from "@apollo/client/link/context";
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  NormalizedCacheObject,
} from "@apollo/client";
import { SubscriptionClient } from "subscriptions-transport-ws";
import { WebSocketLink } from "@apollo/client/link/ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { InMemoryCache } from "@apollo/client";
import { getCurrentToken, getKcClientId, getToken } from "@onmo/auth";

let wsLink: WebSocketLink | null = null;
let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
let subscriptionClient: SubscriptionClient | null = null;

export const API_CFG = JSON.parse(
  import.meta.env.VITE_API_CFG || import.meta.env.REACT_APP_ONMO_CFG || "{}",
);
export enum ApiLegacyName {
  Legacy = "legacy",
  PrimaryLegacy = "primaryLegacy",
}

export const cache = new InMemoryCache({
  typePolicies: {
    User: {
      fields: {
        inventories: {
          keyArgs: ["input", ["filters"]],
          merge(existing = { items: [], total: 0 }, incoming, { args }) {
            if (!args?.input?.page?.offset || args.input.page.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              items: [...existing.items, ...incoming.items],
            };
          },
        },
        activities: {
          keyArgs: ["input", ["category"]],
          merge(existing = { items: [] }, incoming) {
            return {
              ...incoming,
              items: [...existing.items, ...incoming.items],
            };
          },
        },
        following: {
          keyArgs: false,
          merge(existing = { items: [], total: 0 }, incoming, { args }) {
            if (!args?.input?.page?.offset || args.input.page.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              items: [...(existing.items || []), ...incoming.items],
            };
          },
        },
        followers: {
          keyArgs: false,
          merge(existing = { items: [], total: 0 }, incoming, { args }) {
            if (!args?.input?.page?.offset || args.input.page.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              items: [...(existing.items || []), ...incoming.items],
            };
          },
        },
      },
    },
    Query: {
      fields: {
        leaderboard: {
          keyArgs: ["input", ["globalLeaderboardType"]],
          merge(existing = { items: [] }, incoming) {
            return {
              ...incoming,
              items: [...existing.items, ...incoming.items],
            };
          },
        },
      },
    },
  },
});

export const getSubscriptionClient = () => {
  return subscriptionClient;
};

//create Subscription Client with correct token
const createSubscriptionClient = () => {
  const token = getCurrentToken();
  if (!token) return null;
  if (!wsLink) {
    subscriptionClient = new SubscriptionClient(
      API_CFG?.api?.wsApolloUrl || "",
      {
        minTimeout: 10000,
        reconnect: true,
        connectionParams: async () => {
          const accessToken = await getToken();
          return {
            headers: { authorization: accessToken },
          };
        },
        connectionCallback(error, result?) {
          console.log("[subscription] - Connection callback", {
            result,
            error,
          });
        },
      },
      null,
      [],
    );
    wsLink = new WebSocketLink(subscriptionClient);
  }

  return wsLink;
};

export const createApolloClient = () => {
  const httpLink = createHttpLink({
    uri: API_CFG?.api?.httpApolloUrl,
    includeExtensions: true,
  });
  const legacyHttpLink = createHttpLink({
    uri: API_CFG?.api?.legacyHttpApolloUrl,
    includeExtensions: true,
  });
  const primaryLegacyHttpLink = createHttpLink({
    uri: API_CFG?.api?.primaryLegacyHttpApolloUrl,
    includeExtensions: true,
  });
  const isLegacyApi = (api: string) => {
    return (Object.values(ApiLegacyName) as string[]).includes(api);
  };

  const authLink = setContext(async (_, { headers, api }) => {
    const accessToken = await getToken();
    if (!accessToken) {
      const clientId = getKcClientId();
      return {
        headers: {
          ...headers,
          "x-client-id": clientId,
        },
      };
    } else
      return {
        headers: {
          ...headers,
          authorization: !isLegacyApi(api)
            ? `Bearer ${accessToken}`
            : accessToken,
        },
      };
  });
  const mergedHttpLink = ApolloLink.split(
    (operation) => !isLegacyApi(operation.getContext().api),
    ApolloLink.from([authLink, httpLink]),
    ApolloLink.split(
      (operation) => operation.getContext().api === ApiLegacyName.Legacy,
      ApolloLink.from([authLink, legacyHttpLink]),
      ApolloLink.from([authLink, primaryLegacyHttpLink]),
    ),
  );

  wsLink = createSubscriptionClient();

  const link = wsLink
    ? ApolloLink.split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
          );
        },
        wsLink, //use WebSocket for subscription
        mergedHttpLink,
      )
    : mergedHttpLink;

  apolloClient = new ApolloClient({
    queryDeduplication: true,
    link,
    cache: cache,
  });

  return apolloClient;
};

export const client = createApolloClient();
